import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import FinancialImpactSection from './FinancialImpactSection';
import ActivityLogTab from './ActivityLogTab';
import EmailLogTab from './EmailLogTab';
import RemediationTriggerList from './RemediationTriggerList';
import RiskAffectedIdentities from './RiskAffectedIdentities';
import { parseAffectedResources } from '../utils/affectedResources';
import { parseRemediationSteps } from '../utils/remediationSteps';

// $X.XXXM format — always M with 3 decimals, matches the Financial Impact panel
// (FinancialImpactSection) and the assessment-module risk views.
export const formatImpact = (val) => {
    if (val == null) return '—';
    return `$${(Number(val) / 1_000_000).toFixed(3)}M`;
};

// Why a Defender XDR source closed the risk (RiskDetailsDto.defenderClosureDisposition). The wording
// claims no more than the payload supports: a source reporting a finding resolved does not show that
// anyone did the work.
export const CLOSURE_DISPOSITION_LABELS = {
    ResolvedAtSource: 'Resolved at source — Axaris did not verify that the work was done',
    Superseded: 'Superseded by another finding',
};

// The not-reported date is stored in UTC; a value without an offset is read as UTC, never local time.
export const formatNotReportedDate = (val) => {
    if (!val) return null;
    const s = String(val);
    const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// A risk's details in Chrome-style tabs — Overview, What May Break, Financial Impact, Affected …,
// Activity Log, Email Log — exactly as All Risks shows them, so every page that expands a risk shows
// the same thing. `risk` is a RiskDetailsDto (GET /allrisks/details).
//
// The active tab and the GRC "Generate Descriptions" state are controlled when the caller passes them
// (All Risks keeps one tab per risk and one shared GRC state) and kept here otherwise.
// `leadingTabs` / `extraTabs` ({ id, label, icon, render }) add a page's own tabs before / after these.
// `loading`: the risk is still being read - the standard tabs show a spinner instead of partial data;
// a page's own tabs don't depend on it and stay usable.
const RiskDetailTabs = ({
    loading = false,
    risk,
    activeTab: controlledTab,
    onTabChange,
    grcEnrichState: controlledGrc,
    setGrcEnrichState: setControlledGrc,
    leadingTabs = [],
    extraTabs = [],
}) => {
    const apiCall = useApi();
    const riskKey = String(risk.id);

    // Integration-sourced risks (Defender XDR) and GRC control risks carry an
    // affected-object name list in the description; the tab appears only when
    // one exists. Tab label follows the object type (Devices / User Accounts /
    // Azure Resources / Security Incidents …).
    const affectedResources = parseAffectedResources(risk.riskDescription);
    const tabs = [
        ...leadingTabs,
        { id: 'overview',  label: 'Overview',        icon: '📋' },
        { id: 'evolution', label: 'What May Break',  icon: '📈' },
        { id: 'financial', label: 'Financial Impact', icon: '💰' },
        ...(affectedResources ? [{ id: 'devices', label: `Affected ${affectedResources.typeLabel}`, icon: '💻' }] : []),
        { id: 'log',       label: 'Activity Log',     icon: '🕐' },
        { id: 'emaillog',  label: 'Email Log',        icon: '✉️' },
        ...extraTabs,
    ];

    const [ownTab, setOwnTab] = useState(tabs[0].id);
    const activeTab = controlledTab ?? ownTab;
    const setTab = onTabChange ?? setOwnTab;

    const [ownGrc, setOwnGrc] = useState({ loading: false, message: null, isError: false, queued: false });
    const grcEnrichState = controlledGrc ?? ownGrc;
    const setGrcEnrichState = setControlledGrc ?? setOwnGrc;

    const [openScenarioBands, setOpenScenarioBands] = useState({});
    const toggleScenarioBands = (id) =>
        setOpenScenarioBands(prev => ({ ...prev, [id]: !prev[id] }));

    // fr = risk data from engine (monetary impact shown only when engine has run)
    const fr = risk;
    const pageTab = [...leadingTabs, ...extraTabs].find(t => t.id === activeTab);

    const closureLabel = CLOSURE_DISPOSITION_LABELS[risk.defenderClosureDisposition] || null;
    const isOpen = !['closed', 'deleted'].includes((risk.status || '').toLowerCase());
    const notReportedDate = isOpen ? formatNotReportedDate(risk.sourceNotReportedSince) : null;

    return (
        <>
                <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2">
                    {tabs.map(t => {
                        const isActive = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                    isActive
                                        ? 'bg-white text-indigo-700 border-gray-200 border-b-white z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                        : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                }`}
                                style={isActive ? { borderBottomColor: '#ffffff' } : {}}
                            >
                                <span className="text-sm leading-none">{t.icon}</span>
                                <span>{t.label}</span>
                            </button>
                        );
                    })}
                    <div className="flex-1 border-b border-gray-200 self-end h-[1px]" />
                </div>

                {/* Tab content panel */}
                <div className="border border-gray-200 border-t-0 rounded-b-xl rounded-tr-xl bg-white p-4 mb-1">

                    {pageTab && pageTab.render()}

        {!pageTab && loading && (
            <div role="status" aria-live="polite" data-testid="risk-detail-tabs-loading"
                className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
                <span className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-500" aria-hidden="true" />
                Loading the risk details…
            </div>
        )}

        {/* ── Tab 1: Overview ── */}
                    {!loading && activeTab === 'overview' && (
                        <div>
                            {/* Defender XDR source state: neutral information, not a warning */}
                            {closureLabel && (
                                <p data-testid="risk-closure-disposition"
                                    className="mb-3 px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
                                    {closureLabel}
                                </p>
                            )}
                            {notReportedDate && (
                                <p data-testid="risk-source-not-reported"
                                    className="mb-3 px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
                                    {`Not reported by Defender XDR since ${notReportedDate}`}
                                </p>
                            )}

                            {/* Risk Description */}
                            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <h4 className="font-semibold text-gray-700 mb-2">📋 Risk Description</h4>
                                <p className="text-gray-700 text-sm">{risk.riskDescription || 'No description available.'}</p>
                            </div>

                            {/* Crisis Narrative */}
                            {risk.dbCrisisNarrative && (
                                <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                                    <h4 className="font-semibold text-red-900 mb-2">⚠️ Crisis Narrative</h4>
                                    <p className="text-gray-700 text-sm">{risk.dbCrisisNarrative}</p>
                                </div>
                            )}

                            {/* Resolution & Best Practice */}
                            {(risk.dbExpectedBestPractice || risk.dbStepByStepRemediation) && (
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <h4 className="font-semibold text-blue-900 mb-2">✅ Resolution &amp; Expected Best Practice</h4>
                                    {risk.dbExpectedBestPractice && (
                                        <p className="text-sm text-gray-700">{risk.dbExpectedBestPractice}</p>
                                    )}
                                    {(() => {
                                        const cleanedSteps = parseRemediationSteps(risk.dbStepByStepRemediation);
                                        if (cleanedSteps) {
                                            return (
                                                <div className="space-y-1 mt-2">
                                                    {cleanedSteps.map((s, i) => (
                                                        <div key={i} className="text-sm text-gray-800">{i + 1}. {s}</div>
                                                    ))}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}

                            {/* Remediation Trigger */}
                            {risk.dbAxionRemediationTrigger && (
                                <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                    <h4 className="font-semibold text-purple-900 mb-2">🎯 Remediation Trigger</h4>
                                    <RemediationTriggerList trigger={risk.dbAxionRemediationTrigger} />
                                </div>
                            )}

                            {/* Fallback if no overview data */}
                            {!risk.dbCrisisNarrative && !risk.dbExpectedBestPractice && !risk.dbStepByStepRemediation && !risk.dbAxionRemediationTrigger && (
                                <p className="text-gray-400 text-sm text-center py-4">No additional overview data available.</p>
                            )}
                        </div>
                    )}

                    {/* ── Tab 2: What May Break ── */}
                    {!loading && activeTab === 'evolution' && (() => {
                        const SEVERITY_SET = new Set(['critical','high','medium','low']);
                        const parsePredictive = (val) => {
                            if (!val) return { severity: null, description: null };
                            const colonIdx = val.indexOf(':');
                            if (colonIdx > 0) {
                                const mayBeSev = val.slice(0, colonIdx).trim().toLowerCase();
                                if (SEVERITY_SET.has(mayBeSev)) {
                                    return { severity: val.slice(0, colonIdx).trim(), description: val.slice(colonIdx + 1).trim() || null };
                                }
                            }
                            if (SEVERITY_SET.has(val.trim().toLowerCase())) return { severity: val.trim(), description: null };
                            return { severity: null, description: val };
                        };
                        const severityBadgeClass = (sev) => {
                            const s = (sev || '').toLowerCase();
                            if (s === 'critical') return 'bg-red-100 text-red-700 border-red-300';
                            if (s === 'high')     return 'bg-orange-100 text-orange-700 border-orange-300';
                            if (s === 'medium')   return 'bg-yellow-100 text-yellow-700 border-yellow-300';
                            return 'bg-green-100 text-green-700 border-green-300';
                        };
                        const p45  = parsePredictive(risk.dbPredictive45Day);
                        const p90  = parsePredictive(risk.dbPredictive90Day);
                        const p180 = parsePredictive(risk.dbPredictive180Day);
                        const isGrc = risk.riskType === 'GRC';
                        const missingDescriptions = isGrc && (!p45.description || !p90.description || !p180.description);
                        const hasAnyData = risk.riskEvolutionStory || p45.severity || p45.description || p90.severity || p90.description || p180.severity || p180.description;
                        return (
                        <div>
                            {hasAnyData ? (
                                <div className="p-4 rounded-xl border bg-gradient-to-br from-gray-50 to-gray-100">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                        📈 What May Break — Risk Evolution Stories
                                    </h4>

                                    {missingDescriptions && (
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="text-xs text-amber-700">
                                                    {grcEnrichState.message
                                                        ? grcEnrichState.message
                                                        : 'Day-by-day descriptions not yet generated for this GRC risk.'}
                                                </p>
                                                <button
                                                    disabled={grcEnrichState.loading}
                                                    onClick={async () => {
                                                        setGrcEnrichState({ loading: true, message: null, isError: false, queued: false });
                                                        try {
                                                            const res = await apiCall('POST', '/grcrisks/enrich');
                                                            const count = res?.count ?? 0;
                                                            setGrcEnrichState({ loading: false, message: `Queued ${count} risk${count !== 1 ? 's' : ''} for AI analysis. Descriptions will appear as each risk is processed.`, isError: false, queued: true });
                                                        } catch (err) {
                                                            const msg = err?.response?.status === 403
                                                                ? 'Admin permission required.'
                                                                : 'Failed to queue. Please try again.';
                                                            setGrcEnrichState({ loading: false, message: msg, isError: true, queued: false });
                                                        }
                                                    }}
                                                    className="shrink-0 text-xs px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium transition-colors flex items-center gap-1.5"
                                                >
                                                    {grcEnrichState.loading && <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg>}
                                                    {grcEnrichState.loading ? 'Queueing…' : 'Generate Descriptions'}
                                                </button>
                                            </div>
                                            {grcEnrichState.isError && (
                                                <button onClick={() => setGrcEnrichState({ loading: false, message: null, isError: false, queued: false })} className="text-xs text-amber-600 underline mt-1">Dismiss</button>
                                            )}
                                        </div>
                                    )}

                                    {risk.riskEvolutionStory && (
                                        <>
                                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">AI Analysis</p>
                                            <div className="flex items-start gap-3 p-3 bg-orange-50 border border-yellow-200 rounded-lg mb-3">
                                                <p className="text-sm text-gray-700">{risk.riskEvolutionStory}</p>
                                            </div>
                                        </>
                                    )}

                                                    <div className="space-y-3">
                                        {(p45.severity || p45.description) && (
                                            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                <div className="text-yellow-500 text-lg shrink-0">🟡</div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Day 45 — Early Warning</div>
                                                        {p45.severity && <span className={`text-xs font-bold px-2 py-0.5 rounded border ${severityBadgeClass(p45.severity)}`}>{p45.severity.toUpperCase()}</span>}
                                                    </div>
                                                    {p45.description && <>
                                                        <p className="text-xs font-medium text-yellow-600 mb-1">WITHOUT THIS CONTROL IN 45 DAYS:</p>
                                                        <p className="text-sm text-gray-700">{p45.description}</p>
                                                    </>}
                                                </div>
                                            </div>
                                        )}
                                        {(p90.severity || p90.description) && (
                                            <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                                <div className="text-orange-500 text-lg shrink-0">🟠</div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Day 90 — Active Threat Emerging</div>
                                                        {p90.severity && <span className={`text-xs font-bold px-2 py-0.5 rounded border ${severityBadgeClass(p90.severity)}`}>{p90.severity.toUpperCase()}</span>}
                                                    </div>
                                                    {p90.description && <>
                                                        <p className="text-xs font-medium text-orange-600 mb-1">WITHOUT THIS CONTROL IN 90 DAYS:</p>
                                                        <p className="text-sm text-gray-700">{p90.description}</p>
                                                    </>}
                                                </div>
                                            </div>
                                        )}
                                        {(p180.severity || p180.description) && (
                                            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                <div className="text-red-500 text-lg shrink-0">🔴</div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div className="text-xs font-semibold text-red-700 uppercase tracking-wide">Day 180 — Likely Incident / Breach</div>
                                                        {p180.severity && <span className={`text-xs font-bold px-2 py-0.5 rounded border ${severityBadgeClass(p180.severity)}`}>{p180.severity.toUpperCase()}</span>}
                                                    </div>
                                                    {p180.description && <>
                                                        <p className="text-xs font-medium text-red-600 mb-1">WITHOUT THIS CONTROL IN 180 DAYS:</p>
                                                        <p className="text-sm text-gray-700">{p180.description}</p>
                                                    </>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="p-6 text-center text-gray-400 text-sm">
                                    No risk evolution data available for this risk.
                                </div>
                            )}
                        </div>
                        );
                    })()}

                    {/* ── Tab 3: Financial Impact ── */}
                    {!loading && activeTab === 'financial' && (
                        <div>
                            {risk.financialEngineVersion !== 'fair-align-v1.0' && (
                                <div className="p-6 text-center text-gray-400 text-sm">
                                    FAIR-Aligned financial analysis not yet available for this risk.
                                </div>
                            )}

                            {/* Monetary Impact Analysis — FAIR-enriched risks only */}
                            {risk.financialEngineVersion === 'fair-align-v1.0' && risk.finalCalculatedMonetaryImpact != null && <div className="mb-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                <h4 className="font-medium text-emerald-900 mb-3 flex items-center gap-2 flex-wrap">
                                    💰 Monetary Impact Analysis
                                    {fr.impactLow != null && fr.impactHigh != null ? (
                                        <span className="font-bold text-emerald-700">
                                            {formatImpact(fr.impactLow)} – {formatImpact(fr.impactHigh)}+
                                            <span className="ml-1 text-sm font-normal text-emerald-600">
                                                (Most Likely: {formatImpact(fr.finalCalculatedMonetaryImpact)})
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="font-bold text-emerald-700">
                                            ${Math.round(Number(fr.finalCalculatedMonetaryImpact)).toLocaleString()}
                                        </span>
                                    )}
                                    {false && risk.aiRiskType && (
                                        <span className="px-2 py-0.5 bg-emerald-200 text-emerald-800 text-xs rounded font-mono">
                                            {risk.aiRiskType}
                                        </span>
                                    )}
                                    {false && fr.financialEngineVersion && (
                                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                            Engine {fr.financialEngineVersion}
                                        </span>
                                    )}
                                </h4>
                                {/* 5-category breakdown grid — uses fr for Low/High ranges */}
                                {(() => {
                                    const baseExp  = Number(fr.finalCalculatedMonetaryImpact ?? 0);
                                    const baseLow  = Number(fr.impactLow  ?? fr.finalCalculatedMonetaryImpact ?? 0);
                                    const baseHigh = Number(fr.impactHigh ?? fr.finalCalculatedMonetaryImpact ?? 0);
                                    const tabHasBands = fr.impactLow != null && fr.impactHigh != null && baseLow !== baseHigh;
                                    const cats = [
                                        { label: 'Direct Costs',      pct: 0.30, color: 'text-red-600',    badge: 'bg-red-50 border-red-200'     },
                                        { label: 'Bus. Interruption', pct: 0.25, color: 'text-orange-600', badge: 'bg-orange-50 border-orange-200'},
                                        { label: 'Recovery Costs',    pct: 0.20, color: 'text-blue-600',   badge: 'bg-blue-50 border-blue-200'   },
                                        { label: 'Regulatory Fines',  pct: 0.15, color: 'text-purple-600', badge: 'bg-purple-50 border-purple-200'},
                                        { label: 'Reputation Damage', pct: 0.10, color: 'text-pink-600',   badge: 'bg-pink-50 border-pink-200'   },
                                    ];
                                    return (
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                            {cats.map(({ label, pct, color, badge }) => {
                                                const exp  = Math.max(25_000, Math.round(baseExp  * pct));
                                                const low  = Math.max(25_000, Math.round(baseLow  * pct));
                                                const high = Math.max(25_000, Math.round(baseHigh * pct));
                                                return (
                                                    <div key={label} className={`text-center p-2 rounded border ${badge}`}>
                                                        <div className="text-xs font-semibold text-gray-600 mb-1">{label}</div>
                                                        {tabHasBands ? (
                                                            <>
                                                                <div className={`text-sm font-bold ${color} leading-tight`}>{formatImpact(low)} – {formatImpact(high)}+</div>
                                                                <div className="text-xs text-gray-400 mt-0.5">ML: {formatImpact(exp)}</div>
                                                            </>
                                                        ) : (
                                                            <div className={`text-base font-bold ${color}`}>{formatImpact(exp)}</div>
                                                        )}
                                                        <div className="text-xs text-gray-400 mt-0.5">{Math.round(pct * 100)}%</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>}

                            {risk.financialEngineVersion === 'fair-align-v1.0' && <FinancialImpactSection risk={fr} />}

                            {/* Scenario Exposure Bands */}
                            {false && risk.finalCalculatedMonetaryImpact != null && (() => {
                                const conservative = Number(fr.finalCalculatedMonetaryImpact);
                                const combinedFromParts =
                                    fr.sizeMultiplierApplied != null &&
                                    fr.revenueMultiplierApplied != null &&
                                    fr.regulatoryMultiplierApplied != null
                                        ? fr.sizeMultiplierApplied * fr.revenueMultiplierApplied * fr.regulatoryMultiplierApplied
                                        : null;
                                const appliedMult  = combinedFromParts ?? fr.appliedMultiplier ?? 1.8;
                                const divisor      = appliedMult > 0 ? appliedMult : 1.8;
                                const uncappedBase = conservative / divisor;
                                let moderate = uncappedBase * 2.5;
                                let severe   = uncappedBase * 3.5;
                                if (!isFinite(moderate) || isNaN(moderate)) moderate = conservative;
                                if (!isFinite(severe)   || isNaN(severe))   severe   = moderate;
                                moderate = Math.max(moderate, conservative);
                                severe   = Math.max(severe,   moderate);
                                const hasBackendBands = fr.impactLow != null && fr.impactHigh != null;
                                const conLow  = hasBackendBands ? Number(fr.impactLow)  : null;
                                const conHigh = hasBackendBands ? Number(fr.impactHigh) : null;
                                const uncLow  = conLow  != null ? conLow  / divisor : null;
                                const uncHigh = conHigh != null ? conHigh / divisor : null;
                                const modLow  = uncLow  != null ? Math.max(uncLow  * 2.5, conLow)  : null;
                                const modHigh = uncHigh != null ? uncHigh * 2.5 : null;
                                const sevLow  = uncLow  != null ? Math.max(uncLow  * 3.5, moderate) : null;
                                const sevHigh = uncHigh != null ? uncHigh * 3.5 : null;
                                const headerLow  = conLow  ?? conservative;
                                const headerHigh = sevHigh ?? severe;
                                const renderBand = (lo, hi, ml, color) =>
                                    lo != null && hi != null ? (
                                        <div className="text-right">
                                            <div className={`text-sm font-bold ${color} leading-tight`}>{formatImpact(lo)} – {formatImpact(hi)}+</div>
                                            <div className="text-xs text-gray-400 mt-0.5">ML: {formatImpact(ml)}</div>
                                        </div>
                                    ) : (
                                        <span className={`text-sm font-bold ${color}`}>{formatImpact(ml)}</span>
                                    );
                                const isOpen = !!openScenarioBands[riskKey];
                                return (
                                    <div className="mb-4 rounded-lg border border-amber-200 overflow-hidden">
                                        <button
                                            onClick={() => toggleScenarioBands(riskKey)}
                                            className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-2">
                                                {isOpen
                                                    ? <ChevronDown size={16} className="text-amber-700 flex-shrink-0" />
                                                    : <ChevronRight size={16} className="text-amber-700 flex-shrink-0" />
                                                }
                                                <span className="text-sm font-semibold text-amber-900">📊 Scenario Exposure Bands</span>
                                            </div>
                                            <span className="ml-2 flex-shrink-0 text-right">
                                                <span className="text-sm font-bold text-emerald-700">{formatImpact(headerLow)}</span>
                                                <span className="text-gray-400 mx-1">–</span>
                                                <span className="text-sm font-bold text-red-600">{formatImpact(headerHigh)}+</span>
                                            </span>
                                        </button>
                                        {isOpen && (
                                            <div className="px-4 py-4 bg-white space-y-2">
                                                <div className="flex items-center justify-between px-3 py-2 rounded border bg-emerald-50 border-emerald-200">
                                                    <span className="text-xs font-semibold text-emerald-800">Conservative</span>
                                                    {renderBand(conLow, conHigh, conservative, 'text-emerald-700')}
                                                </div>
                                                <div className="flex items-center justify-between px-3 py-2 rounded border bg-amber-50 border-amber-200">
                                                    <span className="text-xs font-semibold text-amber-800">Moderate Exposure</span>
                                                    {renderBand(modLow, modHigh, moderate, 'text-amber-600')}
                                                </div>
                                                <div className="flex items-center justify-between px-3 py-2 rounded border bg-red-50 border-red-200">
                                                    <span className="text-xs font-semibold text-red-800">Severe Exposure</span>
                                                    {renderBand(sevLow, sevHigh, severe, 'text-red-600')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {!loading && activeTab === 'devices' && affectedResources && (
                        // Phase 2: identity links when the risk has them, the prose grid otherwise.
                        <RiskAffectedIdentities riskId={String(risk.id)} affectedResources={affectedResources} />
                    )}
                    {!loading && activeTab === 'log' && (
                        <ActivityLogTab riskId={String(risk.id)} riskType={risk.riskType} />
                    )}
                    {!loading && activeTab === 'emaillog' && (
                        <EmailLogTab riskId={String(risk.id)} />
                    )}

                </div>
        </>
    );
};

export default RiskDetailTabs;
